import * as React from 'react'
import { Loader2, Search } from 'lucide-react'
import type { IrrigationPartDto } from '@lib/dto/housecall-pro.dto'
import type { IrrigationPartKind } from '@lib/enums/irrigation-part-kind.enum'
import { formatMoneyAmount } from '@lib/helpers/money'
import { errorMessage } from '@/helpers/logger'
import { useIrrigationParts } from '@/hooks/useHousecallPro'

/** Enough to choose from without scrolling a phone; the search narrows the rest. */
const MAX_SHOWN = 8

interface PartPickerProps {
  /** Which picker to read. Omitted: every irrigation material. */
  kind?: IrrigationPartKind | undefined
  onPick: (part: IrrigationPartDto) => void
  /** Parts already chosen, left out of the list. */
  excludeUuids?: readonly string[]
  autoFocus?: boolean
}

function hiddenCount(hidden: number): string {
  if (hidden === 1) return '1 more match. Keep typing to narrow it down.'
  return `${String(hidden)} more matches. Keep typing to narrow them down.`
}

function matches(part: IrrigationPartDto, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  return (
    part.name.toLowerCase().includes(needle) ||
    (part.partNumber?.toLowerCase().includes(needle) ?? false)
  )
}

/**
 * A searchable list of parts from the Housecall Pro price book.
 *
 * A search box over a short list rather than a `<select>`: the irrigation
 * price book runs to a hundred and sixty items, and a technician knows
 * "PGV" or "1.5" long before they would find it scrolling.
 */
export function PartPicker({
  kind,
  onPick,
  excludeUuids = [],
  autoFocus,
}: PartPickerProps) {
  const parts = useIrrigationParts(kind)
  const [query, setQuery] = React.useState('')

  function renderList(): React.ReactNode {
    if (parts.isPending) {
      return (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    if (parts.isError) {
      return (
        <p className="py-2 text-xs text-danger">
          {errorMessage(parts.error, 'Parts could not be loaded.')}
        </p>
      )
    }
    if (parts.data.parts.length === 0) {
      return (
        <p className="py-2 text-xs text-muted-text">
          No parts have been read from Housecall Pro yet. Choose the irrigation
          materials category in Settings → Irrigation.
        </p>
      )
    }
    const matching = parts.data.parts.filter(
      (part) => !excludeUuids.includes(part.uuid) && matches(part, query),
    )
    const shown = matching.slice(0, MAX_SHOWN)
    const hidden = matching.length - shown.length
    if (shown.length === 0) {
      return (
        <p className="py-2 text-xs text-muted-text">
          Nothing matches “{query.trim()}”.
        </p>
      )
    }
    return (
      <>
        <ul className="divide-y divide-default">
          {shown.map((part) => (
            <li key={part.uuid}>
              <button
                type="button"
                className="flex w-full items-baseline justify-between gap-3 px-2 py-1.5 text-left text-sm hover:bg-subtle focus-visible:bg-subtle focus-visible:outline-none"
                onClick={() => {
                  onPick(part)
                  setQuery('')
                }}
              >
                <span className="min-w-0 truncate">{part.name}</span>
                <span className="shrink-0 text-xs text-muted-text">
                  {formatMoneyAmount(part.price)}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {hidden === 0 ? null : (
          <p className="px-2 py-1 text-xs text-muted-text">
            {hiddenCount(hidden)}
          </p>
        )}
      </>
    )
  }

  return (
    <div className="rounded-md border border-default bg-surface">
      <label className="flex items-center gap-2 border-b border-default px-2">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-text" />
        <span className="sr-only">Search parts</span>
        <input
          className="h-8 w-full bg-transparent text-sm text-primary-text focus-visible:outline-none"
          placeholder="Search parts"
          value={query}
          // A picker opened on purpose is for typing into straight away.
          autoFocus={autoFocus}
          onChange={(event) => {
            setQuery(event.target.value)
          }}
        />
      </label>
      {kind !== undefined &&
      parts.data?.narrowed === false &&
      parts.data.parts.length > 0 ? (
        <p className="border-b border-default px-2 py-1 text-xs text-muted-text">
          Showing every irrigation material: this picker has no subcategory set
          in Settings.
        </p>
      ) : null}
      <div className="px-1">{renderList()}</div>
    </div>
  )
}
